import type { ChatStep } from "@/lib/api/chat";
import { normalizeChatRoute } from "./navigation-paths";

import {
  buildWalkthroughSteps,
  type WalkthroughStep,
} from "@/lib/chat/walkthrough-steps";

type WalkthroughSeedStep = Pick<ChatStep, "text" | "highlightTarget" | "tourTargets">;

const PAGE_WALKTHROUGH_SEEDS: Record<string, WalkthroughSeedStep[]> = {
  "/": [
    {
      text: "Your dashboard shows key metrics at a glance — features created, tasks completed, decisions logged, and AI sessions this period.",
      tourTargets: [
        {
          target: "dashboard.activityChart",
          description:
            "Activity chart — shows features, tasks, decisions, and sessions over time. Hover bars for exact counts.",
        },
        {
          target: "dashboard.metrics.features",
          description:
            "Features Created — total features your team created this period, with growth trend.",
        },
        {
          target: "dashboard.metrics.tasks",
          description:
            "Tasks Completed — completed tasks this period. Click for a detailed list.",
        },
        {
          target: "dashboard.metrics.decisions",
          description:
            "Decisions Logged — architectural and design decisions recorded by AI agents.",
        },
        {
          target: "dashboard.metrics.sessions",
          description: "AI Sessions — how many AI agent sessions ran on your epics.",
        },
        {
          target: "dashboard.whatsNew",
          description: "What's New — release notes and platform updates.",
        },
        {
          target: "dashboard.activityBreakdown",
          description:
            "Activity Breakdown — period-by-period table of all activity metrics.",
        },
      ],
    },
    {
      text: "Use the interval selector (Weekly / Monthly) to change the time range for all charts and metrics.",
      highlightTarget: "dashboard.intervalSelector",
    },
    {
      text: "Click any metric card with data to open a detail sheet with the full list of items.",
    },
  ],
  "/epics": [
    {
      text: "Each card represents an epic — it shows the team badge, progress bar, feature count, and last edited date. Click any card to open its full detail view with features and tasks.",
      tourTargets: [
        {
          target: "epics.cardGrid",
          description:
            "Epic cards — each card shows an epic's name, description, team, and progress. Click to open.",
        },
        {
          target: "epics.statusFilter",
          description:
            "Filter — toggle between Active epics and including Archived ones.",
        },
        {
          target: "epics.viewToggle",
          description:
            "View toggle — switch between card view and compact list view.",
        },
      ],
    },
  ],
  "/epic-requests": [
    {
      text: "Each card shows a proposal from a team member — including the title, description preview, author, and submission time. Click any card to read the full proposal and leave your vote or comments.",
      tourTargets: [
        {
          target: "epicRequests.newRequest",
          description:
            "New Request — opens a guide showing how to submit a new epic request with the SpecTree request-formulator.",
        },
        {
          target: "epicRequests.search",
          description:
            "Search — filter requests by title to find specific proposals.",
        },
        {
          target: "epicRequests.statusFilter",
          description:
            "Status filter — show/hide requests by status: Pending, Approved, Rejected, or Converted.",
        },
      ],
    },
    {
      text: "To submit a new request, click the 'New Request' button — it shows the CLI command to use the request-formulator agent, which guides you through writing a structured proposal.",
      highlightTarget: "epicRequests.newRequest",
    },
  ],
  "/teams": [
    {
      text: "This page lists every team in your workspace. Each card shows the team name, member count, and a short description.",
      tourTargets: [
        {
          target: "teams.title",
          description: "Teams — this page is where you browse and manage teams in your workspace.",
        },
        {
          target: "teams.cardGrid",
          description: "Team cards — each card represents a team. Click one to open its members and workflow settings. Global admins can click the + button on a product team in the sidebar to add a discipline.",
        },
      ],
    },
  ],
  "epic-detail": [
    {
      text: "The description section contains the epic's purpose, goals, and technical approach. Expand it for full context.",
      highlightTarget: "epic.description.toggle",
    },
    {
      text: "The feature list shows every feature in this epic with status badges and assignees. Click any feature to see its tasks.",
      highlightTarget: "epic.featureList",
    },
    {
      text: "Switch to the Plan tab to see the execution order — features are grouped by phase and dependency.",
      highlightTarget: "epic.tabs.plan",
    },
    {
      text: "Use the Execute button to start AI orchestration, which will implement features phase by phase.",
      highlightTarget: "epic.execute.command",
    },
  ],
  "feature-detail": [
    {
      text: "The properties panel shows status, assignee, complexity, and execution order. Update these to track progress.",
      highlightTarget: "feature.properties.status",
    },
    {
      text: "The task list breaks this feature into actionable work items. Each task has its own status and can be assigned independently.",
      highlightTarget: "feature.taskList",
    },
  ],
};

function normalizeWalkthroughPath(pagePath: string): string {
  return normalizeChatRoute(pagePath);
}

export function getCanonicalWalkthroughSteps(pagePath: string): WalkthroughStep[] {
  const path = normalizeWalkthroughPath(pagePath);

  let walkthroughKey: string | undefined = path;
  if (!PAGE_WALKTHROUGH_SEEDS[walkthroughKey]) {
    if (/^\/epics\/[^/]+\/?$/.test(path)) {
      walkthroughKey = "epic-detail";
    } else if (/^\/features\/[^/]+\/?$/.test(path)) {
      walkthroughKey = "feature-detail";
    } else {
      walkthroughKey = undefined;
    }
  }

  if (!walkthroughKey) {
    return [];
  }

  const walkthroughSeeds = PAGE_WALKTHROUGH_SEEDS[walkthroughKey];
  return walkthroughSeeds ? buildWalkthroughSteps(walkthroughSeeds) : [];
}

export function resolveWalkthroughSteps(
  pagePath: string,
  fallbackSteps: readonly WalkthroughSeedStep[],
): WalkthroughStep[] {
  const canonicalSteps = getCanonicalWalkthroughSteps(pagePath);
  return canonicalSteps.length > 0
    ? canonicalSteps
    : buildWalkthroughSteps(fallbackSteps);
}
